import React, { memo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useScrolled } from '../../hooks/useScrolled'
import { useUIStore } from '../../store/uiStore'
import { useAuthStore } from '../../store/authStore'
import { MegaMenu } from './MegaMenu'
import { AccountDropdown } from '../molecules/AccountDropdown'
import { SearchBar } from '../molecules/SearchBar'
import { CartBadge } from '../atoms/CartBadge'
import { useCartStore, selectTotalItems } from '../../store/cartStore'

export const Header = memo(function Header() {
  const scrolled                    = useScrolled(4)
  const { toggleMobileNav, openSearch } = useUIStore()
  const { isAuthenticated }         = useAuthStore()
  const totalItems                  = useCartStore(selectTotalItems)
  const navigate                    = useNavigate()

  const handleCartClick = useCallback(() => navigate('/cart'), [navigate])

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <header
        className={`sticky top-0 z-30 w-full transition-shadow duration-200 ${
          scrolled ? 'shadow-card-hover' : ''
        }`}
        role="banner"
      >

        {/* ── Utility bar ── */}
        <div className="relative bg-charcoal overflow-hidden">
          {/* Warm accent hairline — gives the bar a branded top-edge */}
          {/* <div
            className="absolute inset-x-0 top-0 h-[2px]"
            style={{ background: 'linear-gradient(90deg, transparent 0%, #D4651A 40%, #E8874A 60%, transparent 100%)', opacity: 0.7 }}
            aria-hidden="true"
          /> */}

          <div className="max-w-screen-2xl mx-auto px-4 md:px-8 flex items-center justify-between h-9 gap-2">

            {/* Free delivery — truck icon + highlighted value */}
            <div className="flex items-center gap-1.5 shrink-0">
              <svg
                className="w-3 h-3 text-royal-orange shrink-0"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                aria-hidden="true"
              >
                <rect x="1" y="3" width="15" height="13" rx="1" />
                <path d="M16 8h4l3 5v4h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              <p className="font-display text-[10px] sm:text-[11px] font-semibold leading-none tracking-wide text-white/55">
                FREE DELIVERY OVER{' '}
                <span className="text-royal-orange font-bold">KES 100,000</span>
              </p>
            </div>

            {/* Contact — phone + email, icon-prefixed, hover lifts to warm tone */}
            <div className="hidden sm:flex items-center gap-3 md:gap-4 min-w-0 overflow-hidden">

              <a
                href="tel:+254708405238"
                className="flex items-center gap-1.5 text-white/45 hover:text-royal-orange-light transition-colors duration-150 focus-visible:outline-none focus-visible:underline whitespace-nowrap group"
              >
                <svg className="w-3 h-3 shrink-0 opacity-55 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.37A16 16 0 0 0 15.54 16l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="font-sans text-[10px] md:text-[11px]">+254 708 405 238</span>
              </a>

              <span className="w-px h-3 bg-white/10 shrink-0" aria-hidden="true" />

              <a
                href="mailto:support@onetech.co.ke"
                className="flex items-center gap-1.5 text-white/45 hover:text-royal-orange-light transition-colors duration-150 focus-visible:outline-none focus-visible:underline truncate max-w-[200px] md:max-w-none group"
              >
                <svg className="w-3 h-3 shrink-0 opacity-55 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span className="font-sans text-[10px] md:text-[11px]">
                  <span className="hidden md:inline">support@onetech.com | onetechcomputers2@gmail.com</span>
                  <span className="md:hidden">support@onetech.com</span>
                </span>
              </a>
            </div>

          </div>
        </div>

        {/* ── Main header — white surface ── */}
        <div className="bg-white border-b border-border-color">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between gap-2 sm:gap-4 h-14 sm:h-16">

              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={toggleMobileNav}
                aria-label="Open navigation menu"
                aria-expanded="false"
                aria-controls="mobile-nav"
                className="lg:hidden flex flex-col items-center justify-center gap-[5px] w-8 h-8 shrink-0 rounded-royal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange"
              >
                <span className="block w-[18px] h-[1.5px] bg-charcoal rounded-full" aria-hidden="true" />
                <span className="block w-[18px] h-[1.5px] bg-charcoal rounded-full" aria-hidden="true" />
                <span className="block w-3 h-[1.5px] bg-charcoal rounded-full self-start" aria-hidden="true" />
              </button>

              {/* Logo */}
              <Link
                to="/"
                aria-label="OneTech Computers — Home"
                className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange rounded-royal"
              >
                <OneTechLogo />
              </Link>

              {/* Search — desktop only */}
              <div className="hidden lg:flex flex-1 max-w-2xl mx-4">
                <SearchBar className="w-full" />
              </div>

              {/* Right actions */}
              <div className="flex items-center gap-0.5 sm:gap-1 ml-auto lg:ml-0">

                {/* Mobile search trigger */}
                <button
                  type="button"
                  onClick={openSearch}
                  aria-label="Search"
                  className="lg:hidden w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-royal text-charcoal hover:text-royal-orange hover:bg-royal-ivory transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange"
                >
                  <svg className="w-[17px] h-[17px] sm:w-[18px] sm:h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </button>

                {/* Wishlist */}
                {isAuthenticated && (
                  <Link
                    to="/account/wishlist"
                    aria-label="My Wishlist"
                    className="hidden sm:flex w-9 h-9 items-center justify-center rounded-royal text-charcoal hover:text-royal-orange hover:bg-royal-ivory transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange"
                  >
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                  </Link>
                )}

                {/* Cart */}
                <button
                  type="button"
                  onClick={handleCartClick}
                  aria-label={`Shopping cart, ${totalItems} item${totalItems !== 1 ? 's' : ''}`}
                  className="relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-royal text-charcoal hover:text-royal-orange hover:bg-royal-ivory transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange"
                >
                  <svg className="w-[17px] h-[17px] sm:w-[18px] sm:h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  <CartBadge count={totalItems} />
                </button>

                {/* Divider */}
                <span className="hidden sm:block w-px h-5 bg-border-color mx-1" aria-hidden="true" />

                {/* Account */}
                <div className="hidden sm:block">
                  <AccountDropdown />
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Mega nav — desktop only ── */}
        <div className="hidden lg:block bg-charcoal">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <MegaMenu />
          </div>
        </div>

      </header>
    </>
  )
})

// ─── Logo ─────────────────────────────────────────────────────────────────────

const OneTechLogo: React.FC = () => (
  <img
    src="/logo/logo.png"
    alt="OneTech Computers"
    className="h-8 sm:h-10 lg:h-12 w-auto object-contain"
    draggable={false}
  />
)